"use client";

import { useCallback, useEffect, useMemo, useState, useTransition, type ReactNode } from "react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { ChevronRight } from "lucide-react";
import { captureEvent } from "@/lib/posthog-client";
import { useRouter } from "@/i18n/navigation";
import type { CityConfig, Event, MapBounds } from "@/lib/types";
import { genreBrowseHref, type GenreBrowseOptions } from "@/lib/genre-calendar";
import { groupEventsByVenue } from "@/lib/utils";
import { dedupeCityJukeboxPlaylist, feedJukeboxEvents, genreJukeboxPlaylists, launchJukeboxAt, resumeIndexAfterLaunch } from "@/lib/jukebox-playlist";
import { AuthProvider } from "../auth/auth-context";
import { useSetSpotifyCitySlug } from "../spotify/use-set-city-slug";
import { useJukeboxReopen } from "../spotify/use-jukebox-reopen";
import { useJukeboxLauncher } from "../spotify/use-jukebox-launcher";
import type { JukeboxLaunchTarget } from "../spotify/spotify-player-context";
import { localizedPath } from "@/lib/seo";
import { EventsGrid } from "./events-grid";
import { GenreJukeboxCta } from "./genre-jukebox-cta";
import { GenreTagLinkModeProvider } from "./feed-genre-filter-context";
import { ViewSwitcher, type ViewMode } from "./view-switcher";
import { useDiscoveryWebMcp } from "../discovery/use-discovery-webmcp";

const GenreJukeboxModal = dynamic(() => import("./genre-jukebox-modal").then((module) => module.GenreJukeboxModal), { ssr: false });
const MapView = dynamic(() => import("../map/map-view").then((module) => module.MapView), { ssr: false });
const MapSidebar = dynamic(() => import("../map/map-sidebar").then((module) => module.MapSidebar), { ssr: false });

export function GenreCalendarFrame({ children }: { children: ReactNode }) {
  return <AuthProvider>{children}</AuthProvider>;
}

export function GenreCalendarViewSwitcher({ base, options }: { base: string; options: GenreBrowseOptions }) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  return <div aria-busy={pending}><ViewSwitcher view={options.view} onViewChange={(view: ViewMode) => {
    startTransition(() => router.push(genreBrowseHref(base, { ...options, view, page: 1 }), { scroll: false }));
  }} onMapHover={() => { void import("../map/map-view"); }} /></div>;
}

export function GenreCalendarEvents({ events, jukeboxEvents, city, locale, genreSlug, genreName, options, base, sidebar, pagination, emptyState, blockedSpotifyIds = [] }: {
  events: Event[]; jukeboxEvents: Event[]; city: CityConfig; locale: string;
  genreSlug: string; genreName: string; options: GenreBrowseOptions; base: string;
  sidebar: ReactNode; pagination: ReactNode; emptyState: ReactNode;
  blockedSpotifyIds?: string[];
}) {
  const tEvents = useTranslations("Events");
  const tJukebox = useTranslations("Jukebox");
  const [jukeboxOpen, setJukeboxOpen] = useState(false);
  const [jukeboxIndex, setJukeboxIndex] = useState(0);
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [showMapSidebar, setShowMapSidebar] = useState(true);
  const citySlug = city.slug;
  useSetSpotifyCitySlug(citySlug);
  useJukeboxReopen(jukeboxOpen, () => setJukeboxOpen(true));
  const { page, date, view } = options;

  useEffect(() => {
    captureEvent("genre_calendar_viewed", { city: citySlug, genre: genreSlug, page, date_filter: date, view });
  }, [citySlug, genreSlug, page, date, view]);

  const { source, playlist } = useMemo(() => genreJukeboxPlaylists(jukeboxEvents, genreSlug, blockedSpotifyIds), [jukeboxEvents, genreSlug, blockedSpotifyIds]);
  const artistCount = playlist.reduce((count, item) => count + item.artists.length, 0);

  // A card's play button opens the jukebox on that bill, inside the genre's playlist.
  const [jukeboxLaunch, setJukeboxLaunch] = useState<JukeboxLaunchTarget | null>(null);
  const [jukeboxLaunchVersion, setJukeboxLaunchVersion] = useState(0);
  const feedPlaylist = useMemo(() => {
    const byId = new Map(source.map((entry) => [entry.event.id, entry]));
    return dedupeCityJukeboxPlaylist(feedJukeboxEvents(jukeboxEvents, view === "grid" ? "grid" : "list", blockedSpotifyIds)
      .flatMap((entry) => byId.get(entry.event.id) ?? []));
  }, [source, jukeboxEvents, view, blockedSpotifyIds]);
  const jukeboxStart = useMemo(
    () => jukeboxLaunch ? launchJukeboxAt(feedPlaylist, source, jukeboxLaunch.event, jukeboxLaunch.artistId, blockedSpotifyIds) : null,
    [jukeboxLaunch, feedPlaylist, source, blockedSpotifyIds],
  );
  const launchJukeboxFromCard = useCallback((target: JukeboxLaunchTarget) => {
    captureEvent("genre_calendar_preview_started", { city: citySlug, genre: genreSlug, page, view, source: "card" });
    setJukeboxLaunch(target);
    setJukeboxLaunchVersion((version) => version + 1);
    setJukeboxOpen(true);
  }, [citySlug, genreSlug, page, view]);
  useJukeboxLauncher(launchJukeboxFromCard);
  useDiscoveryWebMcp({
    events: jukeboxEvents, citySlug, blockedSpotifyIds,
    onPreview: (artist) => {
      const event = jukeboxEvents.find((event) => event.attractions.some((billed) => billed.id === artist.id));
      if (event) launchJukeboxFromCard({ event, artistId: artist.id });
    },
    onJukebox: (event, artist) => launchJukeboxFromCard({ event, artistId: artist?.id }),
  });

  const venueGroups = useMemo(() => groupEventsByVenue(events), [events]);
  const visibleEvents = useMemo(() => !mapBounds ? events : events.filter((event) =>
    event.venue.latitude >= mapBounds.south && event.venue.latitude <= mapBounds.north
    && event.venue.longitude >= mapBounds.west && event.venue.longitude <= mapBounds.east,
  ), [events, mapBounds]);
  const visibleVenueGroups = useMemo(() => groupEventsByVenue(visibleEvents), [visibleEvents]);
  const handleBoundsChanged = useCallback((bounds: MapBounds) => setMapBounds(bounds), []);
  const mobileViewSwitcher = <div className="sm:hidden"><GenreCalendarViewSwitcher base={base} options={options} /></div>;
  const jukeboxCta = artistCount > 0 ? <GenreJukeboxCta
    headline={tJukebox("ctaHeadlineGenre", { genre: genreName, city: city.name })}
    artistCount={artistCount} cityName={city.name}
    onClick={() => {
      captureEvent("genre_calendar_preview_started", { city: citySlug, genre: genreSlug, page, view });
      setJukeboxOpen(true);
    }}
  /> : null;

  return (
    <GenreTagLinkModeProvider value="directory">
    <section onClickCapture={(event) => {
      const anchor = (event.target as Element).closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      const properties = { city: citySlug, genre: genreSlug, page, date_filter: date, view };
      if (anchor.hasAttribute("data-ticket-link")) {
        captureEvent("genre_calendar_ticket_clicked", { ...properties, event_id: anchor.dataset.eventId, destination: new URL(anchor.href).hostname });
      } else if (new URL(anchor.href).pathname.includes("/event/")) {
        captureEvent("genre_calendar_event_clicked", { ...properties, path: new URL(anchor.href).pathname });
      }
    }}>
      {view === "list" ? (
        <div className="lg:flex lg:items-start lg:gap-8">
          <div className="min-w-0 flex-1">
            {jukeboxCta && <div className="mb-6">{jukeboxCta}</div>}
            {events.length > 0 ? <EventsGrid events={events} blockedSpotifyIds={[]} citySlug={citySlug} view="list" fillWidth externalPagination headerTrailing={mobileViewSwitcher} /> : <>{mobileViewSwitcher}{emptyState}</>}
            {pagination}
          </div>
          {sidebar}
        </div>
      ) : (
        <>
          {jukeboxCta && <div className="mb-6">{jukeboxCta}</div>}
          {view === "grid" ? <>
            {events.length > 0 ? <EventsGrid events={events} blockedSpotifyIds={[]} citySlug={citySlug} view="grid" fillWidth externalPagination gridDateHeaders headerTrailing={mobileViewSwitcher} /> : <>{mobileViewSwitcher}{emptyState}</>}
            {pagination}
          </> : <>
            <div className="mb-3 sm:hidden">{mobileViewSwitcher}</div>
            <div role="region" aria-label={tEvents("map")} className="relative flex h-[calc(100vh-200px)] min-h-[480px] overflow-hidden rounded-lg border border-neutral-800">
              <div className="min-w-0 flex-1"><MapView city={city} locale={locale} venueGroups={venueGroups} selectedVenueId={selectedVenueId} onVenueSelect={setSelectedVenueId} onBoundsChanged={handleBoundsChanged} blockedSpotifyIds={[]} /></div>
              {showMapSidebar && <MapSidebar events={visibleEvents} venueGroups={visibleVenueGroups} blockedSpotifyIds={[]} citySlug={citySlug} />}
              <button type="button" onClick={() => setShowMapSidebar((show) => !show)}
                className={`absolute top-3 hidden h-8 w-8 items-center justify-center rounded-full border border-neutral-700 bg-dark/90 text-neutral-400 transition-colors hover:border-neutral-500 hover:text-white lg:flex ${showMapSidebar ? "right-[25rem]" : "right-3"}`}
                title={showMapSidebar ? tEvents("hideSidebar") : tEvents("showSidebar")} aria-expanded={showMapSidebar}>
                <ChevronRight aria-hidden="true" className={`h-3.5 w-3.5 ${showMapSidebar ? "" : "rotate-180"}`} />
              </button>
            </div>
            {events.length === 0 && emptyState}
          </>}
        </>
      )}
      {jukeboxOpen && <GenreJukeboxModal key={jukeboxLaunchVersion} playlist={jukeboxStart?.playlist ?? playlist} genreName={genreName} selectedGenres={[]} citySlug={citySlug}
        playlistHref={localizedPath(genreBrowseHref(base, options), locale)}
        initialIndex={jukeboxStart?.index ?? jukeboxIndex} initialArtistId={jukeboxStart?.artistId}
        onClose={(index) => { setJukeboxIndex(resumeIndexAfterLaunch(jukeboxStart, playlist, index)); setJukeboxLaunch(null); setJukeboxOpen(false); }} />}
    </section>
    </GenreTagLinkModeProvider>
  );
}
